import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Sparkles, ChevronLeft, RefreshCcw } from "lucide-react";
import { celebrate } from "../lib/confetti";

const STEPS = [
  {
    key: "family",
    title: "Quel type de parfum t'attire ?",
    options: [
      { value: "eau-de-parfum",   label: "Eau de parfum",       hint: "Un sillage porté sur la peau" },
      { value: "parfum-solide",   label: "Parfum solide",       hint: "Voyage, discrétion, cire naturelle" },
      { value: "brume",           label: "Brume capillaire",    hint: "Un halo léger dans les cheveux" },
      { value: "bougie",          label: "Bougie parfumée",     hint: "Une atmosphère pour la maison" },
      { value: "parfum-ambiance", label: "Parfum d'ambiance",   hint: "Un diffuseur pour toutes les pièces" },
    ],
  },
  {
    key: "gender",
    title: "Pour qui est-il destiné ?",
    // Shown only when family === "eau-de-parfum"
    conditional: (a) => a.family === "eau-de-parfum",
    options: [
      { value: "femmes",  label: "Femmes",  hint: "Notes florales, ambrées, gourmandes" },
      { value: "hommes",  label: "Hommes",  hint: "Notes boisées, cuirées, ambrées" },
      { value: "unisexe", label: "Unisexe", hint: "Ni féminin ni masculin, universel" },
      { value: "enfants", label: "Enfants", hint: "Notes tendres, douces, gourmandes" },
    ],
  },
  {
    key: "mood",
    title: "Quelle humeur veux-tu incarner ?",
    // Only meaningful for EDP / edition-limitee since sub-scoring uses
    // perfume_notes which only exist on EDPs. For other families we skip
    // this step and jump straight to results.
    conditional: (a) => a.family === "eau-de-parfum",
    options: [
      { value: "frais",     label: "Frais & lumineux", tags: ["Bergamote","Citron","Yuzu","Verveine","Menthe","Néroli","Pamplemousse"] },
      { value: "floral",    label: "Floral & tendre",  tags: ["Rose","Jasmin","Fleur d'oranger","Pivoine","Magnolia","Muguet"] },
      { value: "gourmand",  label: "Gourmand & câlin", tags: ["Vanille","Miel","Coco","Amande","Cerise","Barbe à papa","Cacao","Fève tonka"] },
      { value: "boise",     label: "Boisé & racé",     tags: ["Santal","Oud","Cèdre","Vétiver","Bois de rose","Ambroxan","Patchouli"] },
      { value: "oriental",  label: "Oriental & épicé", tags: ["Ambre","Cuir","Safran","Cannelle","Poivre","Cardamome","Encens"] },
    ],
  },
];

function scoreProduct(p, answers) {
  if (answers.family && p.sub_family !== answers.family && !(answers.family === "eau-de-parfum" && p.sub_family === "edition-limitee")) return -1;
  if (answers.gender && answers.family === "eau-de-parfum" && p.gender && p.gender !== answers.gender) return -1;
  let score = 0;
  if (answers.mood && p.perfume_notes) {
    const step = STEPS.find((s) => s.key === "mood");
    const tags = step.options.find((o) => o.value === answers.mood)?.tags || [];
    const allNotes = [
      ...(p.perfume_notes.top || []),
      ...(p.perfume_notes.heart || []),
      ...(p.perfume_notes.base || []),
    ].join(" ").toLowerCase();
    for (const t of tags) {
      if (allNotes.includes(t.toLowerCase())) score += 1;
    }
  }
  return score;
}

export default function ParfumQuiz() {
  const [products, setProducts] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Trouve ton parfum — Beckyliss";
    fetchProducts({ category: "parfums" }).then(setProducts);
  }, []);

  // Fire confetti once results appear
  useEffect(() => {
    if (done) celebrate();
  }, [done]);

  const visibleSteps = useMemo(
    () => STEPS.filter((s) => !s.conditional || s.conditional(answers)),
    [answers]
  );
  const current = visibleSteps[step];

  const recommendations = useMemo(() => {
    if (!done || products.length === 0) return [];
    return products
      .map((p) => ({ p, score: scoreProduct(p, answers) }))
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((x) => x.p);
  }, [done, products, answers]);

  const pickAnswer = (value) => {
    const newAnswers = { ...answers, [current.key]: value };
    setAnswers(newAnswers);
    // Recompute visible steps with the new answer
    const nextVisible = STEPS.filter((s) => !s.conditional || s.conditional(newAnswers));
    if (step + 1 >= nextVisible.length) {
      setDone(true);
    } else {
      setStep(step + 1);
    }
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
  };

  const progress = ((step + (done ? 1 : 0)) / Math.max(1, visibleSteps.length)) * 100;

  return (
    <div className="fade-in-up" data-testid="parfum-quiz-page">
      {/* HERO */}
      <section className="relative overflow-hidden hero-bg-gradient border-b border-bl-border">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
          <Link to="/categorie/parfums" className="inline-flex items-center gap-2 text-sm overline">
            <ChevronLeft size={14} /> Retour aux parfums
          </Link>
          <div className="mt-8 max-w-2xl">
            <span className="overline flex items-center gap-2">
              <Sparkles size={12} strokeWidth={1.5} /> Mini-quiz
            </span>
            <h1 className="font-serif font-light text-5xl md:text-6xl text-bl-text mt-3 leading-[1.05]">
              Trouve ton <em className="italic text-bl-dore">parfum</em>.
            </h1>
            <p className="mt-6 text-base font-light text-bl-textSoft leading-relaxed">
              Trois questions pour révéler le parfum Beckyliss qui te ressemble —
              parmi nos 34 créations : eaux de parfum, brumes, bougies et parfums d'ambiance.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20">
        {!done && current && (
          <div data-testid={`parfum-quiz-step-${current.key}`}>
            {/* Progress */}
            <div className="mb-10">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-bl-textSoft mb-2">
                <span>Étape {step + 1} · {visibleSteps.length}</span>
                <span>{Math.round(progress)} %</span>
              </div>
              <div className="h-1 bg-bl-border">
                <div
                  className="h-full bg-bl-mauveDeep transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-10">
              {current.title}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {current.options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => pickAnswer(opt.value)}
                  data-testid={`parfum-quiz-option-${opt.value}`}
                  className="text-left border border-bl-border p-6 hover:border-bl-mauveDeep hover:bg-bl-rose/20 transition-all group"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-serif text-xl text-bl-text group-hover:text-bl-mauveDeep">
                      {opt.label}
                    </span>
                    <span className="text-bl-dore text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                      Choisir →
                    </span>
                  </div>
                  {opt.hint && (
                    <p className="mt-2 text-sm text-bl-textSoft leading-relaxed">{opt.hint}</p>
                  )}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="mt-8 text-xs uppercase tracking-[0.18em] text-bl-textSoft hover:text-bl-mauveDeep"
                data-testid="parfum-quiz-back"
              >
                ← Question précédente
              </button>
            )}
          </div>
        )}

        {done && (
          <div data-testid="parfum-quiz-results">
            <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
              <div>
                <span className="overline flex items-center gap-2">
                  <Sparkles size={12} /> Ta sélection
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-bl-text mt-3 leading-tight">
                  Voici les parfums <em className="italic text-bl-dore">faits pour toi</em>.
                </h2>
              </div>
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 text-sm border border-bl-mauveDeep text-bl-mauveDeep px-5 py-2.5 hover:bg-bl-mauveDeep hover:text-white transition-colors"
                data-testid="parfum-quiz-restart"
              >
                <RefreshCcw size={14} /> Recommencer
              </button>
            </div>

            {recommendations.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {recommendations.map((p) => (
                  <ProductCard key={p.slug} product={p} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 border border-bl-border">
                <p className="font-serif text-2xl text-bl-text">Aucun parfum ne matche.</p>
                <p className="mt-4 text-sm text-bl-textSoft">
                  Recommence avec d'autres réponses ou explore la catégorie complète.
                </p>
                <Link
                  to="/categorie/parfums"
                  className="mt-6 inline-block border border-bl-mauveDeep px-6 py-3 text-sm hover:bg-bl-mauveDeep hover:text-white"
                >
                  Voir tous les parfums
                </Link>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
